import React, { useMemo } from "react";

import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";

import { PageBanner, Table } from "@performyard/py-components";
import type { ColDef, ICellRendererParams, TableRef } from "@performyard/py-components";

import { Divider } from "../../../../../components/Divider";
import { GroupHeaderRow } from "./GroupHeaderRow";

import type { DecisionRow } from "../../../types";
import type { DecisionTableContext, IncreaseValues, ManagerGroup } from "../types";

import { buildGroupRowDataWithExpansion, computeGroupBudgetFigures, mapGroupUsersToDecisionRows } from "../utils";

import { buildAuditLogEntries } from "../../SharedComponents/AuditLog/buildAuditLogEntries";
import { AuditLogModal } from "../../SharedComponents/AuditLogModal";
import { BudgetSummaryCard } from "../../SharedComponents/BudgetSummaryCard";

export type MyDecisionsGroupProps = {
	group: ManagerGroup;
	userID?: string;
	isRefreshingGroup: boolean;
	expandedRowId: string | null;
	baseGridContext: Omit<DecisionTableContext, "expandedRowId" | "onExpand" | "onCollapse" | "managerId">;
	onExpand: (rowId: string) => void;
	onCollapse: () => void;
	recommendedIncreasePercent: number;
	increaseValues: IncreaseValues;
	onSubmitDecisions: () => void;
	gridRefs: React.MutableRefObject<Record<string, TableRef<DecisionRow>>>;
	columnDefs: ColDef<DecisionRow>[];
	notesPayloadKey: (row: DecisionRow) => string;
	fullWidthCellRenderer: (params: ICellRendererParams<DecisionRow>) => React.ReactNode;
	showValidationBanner: boolean;
	cycleStatus: string;
	isAuditLogOpen: boolean;
	onOpenAuditLog: () => void;
	onCloseAuditLog: () => void;
};

/** One manager's decision table, with its budget summary, audit log and submit action. */
export const MyDecisionsGroup = ({
	group,
	userID,
	isRefreshingGroup,
	expandedRowId,
	baseGridContext,
	onExpand,
	onCollapse,
	recommendedIncreasePercent,
	increaseValues,
	onSubmitDecisions,
	gridRefs,
	columnDefs,
	notesPayloadKey,
	fullWidthCellRenderer,
	showValidationBanner,
	cycleStatus,
	isAuditLogOpen,
	onOpenAuditLog,
	onCloseAuditLog,
}: MyDecisionsGroupProps): React.ReactElement => {
	const groupUsers = group.users;
	const groupSubmitted = groupUsers.every((u) => u.status !== "pending");
	const groupManagerId = groupUsers[0]?.managerId ?? null;
	const currentManagerViewing = groupUsers[0]?.currentManagerViewing ?? "";
	const groupManagerName = groupUsers[0]?.managerName ?? null;
	const baseGroupRowData = useMemo(() => mapGroupUsersToDecisionRows(groupUsers, userID), [groupUsers, userID]);

	// Must stay referentially stable across draft edits: AG Grid re-sorts on any row identity change, and New
	// Salary now sorts on the draft, so churning this array re-orders rows while a manager types.
	const groupRowData = useMemo(
		() => buildGroupRowDataWithExpansion(baseGroupRowData, expandedRowId, notesPayloadKey),
		[baseGroupRowData, expandedRowId, notesPayloadKey]
	);

	const groupGridContext: DecisionTableContext = {
		...baseGridContext,
		managerId: group.managerId,
		expandedRowId,
		onExpand,
		onCollapse,
	};

	const figures = computeGroupBudgetFigures(groupUsers, baseGroupRowData, increaseValues, recommendedIncreasePercent);

	const getActorNameForRow = (row: DecisionRow): string | null => {
		const approverId = row.approver;
		if (!approverId) return null;
		if (approverId === currentManagerViewing) {
			return groupManagerName?.trim() || null;
		}
		return null;
	};

	const auditLogEntries = buildAuditLogEntries(group.managerId, baseGroupRowData, getActorNameForRow);

	return (
		<div className="mt-6 mb-8">
			<GroupHeaderRow
				userID={userID}
				groupManagerId={groupManagerId}
				groupManagerName={groupManagerName}
				groupSubmitted={groupSubmitted}
				isRefreshingGroup={isRefreshingGroup}
				onSubmitDecisions={onSubmitDecisions}
				cycleStatus={cycleStatus}
			/>

			<div className="my-4">
				<Divider color="neutral" />
			</div>
			<div className="pt-4 pr-4 pl-4">
				<BudgetSummaryCard
					figures={figures}
					onViewAuditLog={onOpenAuditLog}
					page={"my-decisions"}
					cycleStatus={cycleStatus}
				/>

				{showValidationBanner && (
					<div className="my-4">
						<PageBanner
							icon={faTriangleExclamation}
							color="red"
							mainTitle="Please enter an increase for each employee. All increases must be equal to or greater than 0."
						/>
					</div>
				)}

				{isAuditLogOpen && <AuditLogModal onClose={onCloseAuditLog} entries={auditLogEntries} />}

				<div className="mt-4 [&_.ag-header]:!bg-white [&_.ag-root-wrapper]:!border-none">
					<Table<DecisionRow>
						ref={(instance) => {
							if (instance) {
								gridRefs.current[group.managerId] = instance;
								return;
							}
							delete gridRefs.current[group.managerId];
						}}
						rowData={groupRowData}
						columnDefs={columnDefs}
						context={groupGridContext}
						autoHeight
						animateRows={true}
						getRowId={(params) => params.data._id}
						rowHeight={48}
						headerHeight={48}
						fullWidthCellRenderer={fullWidthCellRenderer}
						isFullWidthRow={(params) => params.rowNode.data?.superRow === true}
						getRowHeight={(params) => (params.data?.superRow ? 220 : 48)}
					/>
				</div>
			</div>
		</div>
	);
};
